/* eslint-disable @typescript-eslint/no-unused-vars */
import * as THREE from 'three';
import { Upload } from 'tus-js-client';

import { mutateWithVariables, query, queryWithVariables } from '@/services/api/api';
import { Model, Model2 } from '@/types/api/Model/Model';
import { Component } from '@/types/api/Model/Component';
import { FileType3D, FileTypeImage, UploadCategory } from '@/types/enum/upload';
import { BoneData, EmbossingExportData, FFDExportData } from '@/services/api/templateService';
import * as THREEEnum from '@/types/enum/three';
import { ProjectionCategory } from '@/types/enum/editor';
import { Vector3 } from '@/types/api/Utility/Vector3';
import * as MathAngle from '@/utils/angle';
import { CurveCategory } from '@/types/api/Model/Curve/CurveCategory';
import { CurveType } from '@/types/api/Model/Curve/CurveType';
import { Curve } from '@/types/api/Model/Curve/Curve';
import { Camera } from '@/types/api/Model/Camera';
import { ProductQuality } from '@/types/enum/template';
import { MeshTreeData, MeshTreeDataItem } from '@/types/ui/MeshTreeData';
import * as UploadTypes from '@/types/enum/upload';
import { Color } from '@/types/api/Model/Attributes/Color';
import { Engraving } from '@/types/api/Model/Attributes/Engraving';
import { Print } from '@/types/api/Model/Attributes/Print';
import * as ComponentService from '@/services/api/componentService';
import { Resolution_Level } from '@/types/api/Model/ResolutionLevel';
import { Size } from '@/types/api/Model/Attributes/Size';
import { Size as UtilitySize } from '@/types/api/Utility/Size';
import { Power } from '@/types/api/Model/Attributes/Power';
import { File as UtilityFile } from '@/types/api/Utility/File';
import { Video } from '@/types/api/Utility/Video';
import { Product } from '@/types/api/Model/Product';
import * as ModelTypes from '@/types/enum/modelParameter';
import { BasePriceDto, BodyPartDto } from '@/types/api/Model/ProductUpsertRequest';
export type MeshTreeDataWithUid = MeshTreeData & { uid?: number };
import Auth from './auth';
import { FenceType } from '@/types/enum/order';
import { LocalColorType } from '@/types/constants/colors';

/* eslint-disable @typescript-eslint/no-explicit-any*/
export type ProductType = {
  id: number | null
}

export type BodyPart = {
  id: number | null
}

export type Thumbnail = {
  name: string;
  content: string;
  type: string
}

export type BasePrices = {
  counter: string;
  price: number
}

export interface UploadNewProductType {
  packageIds: number[];
  name: string;
  description: string;
  productType: ProductType | null;
  bodyPart: BodyPartDto | null;
  thumbnail: Thumbnail;
  basePrices: BasePriceDto[];
  treeData: MeshTreeDataItem[];
  category: string
  productTypeAttributeId?: number | null;
}

export const createProduct = async ({
  productName,
  thumbnail,
  sampleImagePaths,
  videos,
  productType,
  description,
  basePrice,
}: {
  productName: string;
  thumbnail: UtilityFile;
  sampleImagePaths: UtilityFile[];
  videos: Video[];
  productType: UploadTypes.ProductType;
  description: string;
  basePrice: number;
}): Promise<{
  modelId: number | null;
  productId: number | null;
}> => {
  const mutationCreateProductString = `
    mutation ($product: ProductInput!) {
      createProduct(product: $product)
    }
  `;
  sampleImagePaths = sampleImagePaths
    .filter((t) => {
      return t.name !== thumbnail.name;
    });

  const data = {
    product: {
      addressedDiseases: [],
      basePrices: [],
      bodyPart: null,
      defaultCharacteristics: [],
      description: description,
      name: productName,
      sampleImagePaths: sampleImagePaths,
      supportedGridTypes: [],
      thumbnail: thumbnail,
      // ... video and videoThumbnail data here....
      videos: videos,
      type: {
        name: productType,
        attributes: [],
      },
      packages: [],
    },
  };

  const createResult = await mutateWithVariables(
    mutationCreateProductString,
    data
  );

  const productId = createResult.data.createProduct;

  const getModelsResult = await queryWithVariables(
    `
        query ($productId: Long!) {
          modelsByProductId (productId: $productId) {
            modelId
          }
        }
      `,
    {
      productId: productId,
    }
  );

  const modelId = getModelsResult.data.modelsByProductId[0].modelId;

  if (!modelId) {
    return {
      modelId: -1,
      productId: -1,
    };
  }

  return {
    modelId,
    productId: productId,
  };
};

export const uploadNewProduct = async ({
  packageIds,
  name,
  description,
  productType,
  bodyPart,
  thumbnail,
  basePrices,
  treeData,
  category,
  productTypeAttributeId
}: UploadNewProductType): Promise<{
  modelId: number,
  productId: number,
  componentIds?: ComponentIdsType[]
}
> => {

  // mutation query 
  const mutationCreateProductString = `
    mutation ($param: ProductUpsertRequestInput!) {
      createProduct(productUpsertRequest: $param) {
        productId
        name
        basePrices {
          country
          price
        }
        productTypeAttributeId
      }
    }
  `;

  // initial params 
  const params = {
    param: {
      packageIds,
      name,
      description,
      productType,
      bodyPart,
      thumbnail,
      basePrices,
      productTypeAttributeId
    }
  }

  // execute mutation query 
  const productResult = await mutateWithVariables(mutationCreateProductString, params);

  // validate result 
  if (!productResult || !productResult.data?.createProduct) {
    console.warn('Create Product error!')
    return { modelId: -1, productId: -1 };
  }

  const { productId } = productResult.data.createProduct; //get productId
  const modelResult = await getModelsByProductId(productId) // get model
  const { modelId } = modelResult[0]; // get modelId

  // return if modelId null or undefined 
  if (!modelId) {
    return {
      modelId: -1,
      productId: -1,
    };
  }

  // upload file process 
  const componentIds = await handleUploadFileList(modelId, treeData, category);

  // return result 
  return {
    modelId,
    productId,
    componentIds
  };
}

const handleUploadFileList = async (
  modelId: number,
  treeData: MeshTreeDataItem[],
  category: string
): Promise<ComponentIdsType[]> => {
  const uploadList = new Map<string, Component | null>();
  let componentIds: ComponentIdsType[] = []

  for (const mesh of treeData.filter((t) => t.filename !== null)) {
    uploadList.set(mesh.filename as string, null);

    await uploadFileAsync(
      mesh,
      modelId.toString(),
      uploadList,
      category
    );
  }

  for (const component of uploadList.values()) {
    if (component) {
      const mesh = treeData.find((x) => x.uuid == component.uuid);
      if (mesh) {
        await ComponentService.changeThumbnail(
          component.componentId,
          component.uuid,
          mesh.thumbnail,
          mesh.thumbnailFiletype,
          mesh.thumbnailUrl
        );

        // get componentId & uuid to store with component size 
        const getComponent = {
          componentId: component.componentId,
          uuid: component.uuid,
        }

        componentIds = [...componentIds, getComponent]
      }
    }
  }

  return componentIds;
}

export const uploadFileAsync = async (
  mesh: MeshTreeDataItem,
  modelId: string,
  uploadList: Map<string, Component | null>,
  category: string
): Promise<boolean> => {
  if (!mesh.filename || !mesh.url) {
    return false;
  }

  const file = await fetch(mesh.url).then((r) => r.blob());
  return new Promise((resolve, reject) => {
    const upload = new Upload(file, {
      endpoint: import.meta.env.VITE_BACKEND_FILE_UPLOAD_URL,
      retryDelays: [0, 1000, 3000, 5000],
      headers: {
        'Access-Control-Allow-Origin': import.meta.env.VITE_REDIRECT_URL ?? '',
      },
      metadata: {
        filename: mesh.filename as string,
        modelId: modelId,
        uuid: mesh.uuid,
        category: category,
      },
      onError: function (error) {
        console.error('Failed because: ' + error);
        reject(error);
      },
      onProgress: function (bytesUploaded, bytesTotal) {
        const percentage = ((bytesUploaded / bytesTotal) * 100).toFixed(2);
      },
      onSuccess: function (res) {
        const component = JSON.parse(res.lastResponse.getBody()) as Component;
        if (this.metadata) uploadList.set(this.metadata['filename'], component);
        resolve(true);
      },
    });

    // Check if there are any previous uploads to continue.
    upload.findPreviousUploads().then(function (previousUploads) {
      // Found previous uploads so we select the first one.
      if (previousUploads.length) {
        upload.resumeFromPreviousUpload(previousUploads[0]);
      }

      // Start the upload
      upload.start();
    }).catch(reject);
  });
};

export const upload = async (
  uuid: string,
  name: string,
  mesh: string,
  filename: string,
  filetype: string,
  position: THREE.Vector3 = new THREE.Vector3(0, 0, 0),
  rotation: THREE.Euler = new THREE.Euler(0, 0, 0),
  scale: THREE.Vector3 = new THREE.Vector3(1, 1, 1),
  componentType: UploadCategory,
  resolutionLevel = 1,
  thumbnailContent: string | null = null,
  thumbnailType = '.jpg',
  boneList: THREE.Bone[] = [],
  parentId: number | null = null,
  productId: number | null = null,
  customerId: number | null = null,
  resellerId: number | null = null,
  baseModelId: number | null = null,
  modelNotice: string | null = null,
  componentId: number | null = null,
  resolutionId: number | null = null
): Promise<number> => {
  const mutationQueryString = `
        mutation (
          ${modelNotice ? `$modelNotice: String,` : ''}
          ${baseModelId ? `$baseModelId: Long,` : ''}
          ${productId ? `$productId: Long,` : ''}
          ${resellerId ? `$resellerId: Long,` : ''}
          ${customerId ? `$customerId: Long,` : ''}
          ${componentId ? `$componentId: Long,` : ''}
          $uuid: String!,
          $name: String,
          $positionX: Float!,
          $positionY: Float!,
          $positionZ: Float!,
          $rotationX: Float!,
          $rotationY: Float!,
          $rotationZ: Float!,
          $scaleX: Float!,
          $scaleY: Float!,
          $scaleZ: Float!,
          $componentType: String!,
          ${resolutionId ? `$resolutionId: Long,` : ''}
          $resolutionLevel: Int!,
          $meshBase64: String!,
          $filename: String!,
          $filetype: String!,
          ${parentId ? `parentId: Long,` : ''}
          ${thumbnailContent ? 'thumbnailContent: String!,' : ''}
          ${thumbnailContent ? 'thumbnailType: String!,' : ''}
          $boneList: [MarkingPointPositionInput!]!,
          $curveList: [CurveInput!]!
          ) {
          upload (
            input: {
              ${customerId ? `customerId: $customerId,` : ''}
              ${resellerId ? `resellerId: $resellerId,` : ''}
              ${productId ? `productId: $productId,` : ''}
              ${baseModelId ? `baseModelId: $baseModelId,` : ''}
              ${modelNotice ? `modelNotice: $modelNotice,` : ''}
              components: [
                {
                  ${componentId ? `componentId: $componentId,` : ''}
                  uuid: $uuid,
                  name: $name,
                  attributes: [],
                  position: {
                    xValue: $positionX,
                    yValue: $positionY,
                    zValue: $positionZ
                  },
                  rotation: {
                    xValue: $rotationX,
                    yValue: $rotationY,
                    zValue: $rotationZ
                  },
                  scale: {
                    xValue: $scaleX,
                    yValue: $scaleY,
                    zValue: $scaleZ
                  },
                  type: {
                    name: $componentType
                  },
                  resolutions: [{
                    ${resolutionId ? `resolutionId: $resolutionId,` : ''}
                    level: $resolutionLevel,
                    meshBase64: {
                      content: $meshBase64,
                      name: $filename,
                      type: $filetype
                    }
                  }],
                  markingPoints: {
                    positions: $boneList
                  },
                  ${thumbnailContent ? `thumbnail: {content: $thumbnailContent, name: "thumbnail", type: $thumbnailType},` : ''}
                  ${parentId ? `parent: {componentId: $parentId},` : ''},
                  curves: $curveList
                }
              ]
            }
          ) { long }
        }
      `;

  const data = {
    modelNotice: modelNotice ? modelNotice : '',
    baseModelId: baseModelId ? baseModelId : 0,
    productId: productId ? productId : 0,
    resellerId: resellerId ? resellerId : 0,
    customerId: customerId ? customerId : 0,
    componentId: componentId ? componentId : 0,
    uuid: uuid,
    name: name,
    positionX: position.x,
    positionY: position.y,
    positionZ: position.z,
    rotationX: MathAngle.toDegrees(rotation.x),
    rotationY: MathAngle.toDegrees(rotation.y),
    rotationZ: MathAngle.toDegrees(rotation.z),
    scaleX: MathAngle.toDegrees(scale.x),
    scaleY: MathAngle.toDegrees(scale.y),
    scaleZ: MathAngle.toDegrees(scale.z),
    componentType: componentType.toString(),
    resolutionId: resolutionId ? resolutionId : 0,
    resolutionLevel: resolutionLevel,
    meshBase64: mesh,
    filename: filename,
    filetype: `.${filetype}`,
    parentId: parentId,
    thumbnailContent: thumbnailContent,
    thumbnailType: thumbnailType,
    boneList: boneList.map((item) => {
      return {
        markingPoint: {
          name: item.name,
        },
        position: {
          xValue: item.position.x,
          yValue: item.position.y,
          zValue: item.position.z,
        },
      };
    }),
    curveList: [],
  };

  const result = await mutateWithVariables(mutationQueryString, data);
  return result.data.upload.long;
};

export const createFromTemplate = async (
  templateId: number,
  name = '',
  description = '',
): Promise<number> => {
  const variableDeclarations = [`$modelId: Long!`];
  const inputFields = [`modelId: $modelId`];
  const variables: Record<string, any> = { modelId: templateId };

  if (name) {
    variableDeclarations.push(`$newName: String`);
    inputFields.push(`newName: $newName`);
    variables.newName = name;
  }

  if (description) {
    variableDeclarations.push(`$newDescription: String`);
    inputFields.push(`newDescription: $newDescription`);
    variables.newDescription = description;
  }

  const mutationQueryString = `
    mutation (${variableDeclarations.join(', ')}) {
      copyModel(
        input: {
          ${inputFields.join(',\n')}
        }
      ) {
        model {
          modelId
        }
      }
    }
  `;
  const result = await mutateWithVariables(mutationQueryString, variables);
  return result.data.copyModel.model?.modelId;
};

const modelQueryParameter = `modelId,
    customer {
      userId, username, firstname, lastname, email
    },
    notice,
    components {
      componentId,
      uuid,
      name,
      attributes {attributeId, additionalPrice, components {componentTypeId, name}, date, visualOnly, attributeType},
      resolutions {level, meshBase64{content, name, type}, resolutionId},
      markingPoints {
        polylineId,
        positions{
          mppId,
          position { xValue, yValue, zValue },
          markingPoint { markingPointId, name }
        }
      },
      position {xValue,  yValue, zValue},
      rotation { xValue, yValue, zValue },
      scale { xValue, yValue, zValue },
      type { componentTypeId, name },
      curves {
        curveId,
        name,
        category,
        closed,
        points { xValue, yValue, zValue },
        position { xValue, yValue, zValue },
        rotation { xValue, yValue, zValue },
        scale { xValue, yValue, zValue },
        type
      },
      parent { componentId, uuid },
      thumbnail { name, type, content },
      basePrice,
      unit
    },
    basedOn { modelId },
    curves {
      curveId,
      name,
      category,
      closed,
      points { xValue, yValue, zValue },
      position { xValue, yValue, zValue },
      rotation { xValue, yValue, zValue },
      scale { xValue, yValue, zValue },
      type
    },
    cameras {
      cameraId,
      name,
      position { xValue, yValue, zValue },
      rotation { xValue, yValue, zValue }
    },
    customerAttributes { characteristicId,  date },
    product {
      productId,
      name,
      description,
      basePrices {
       country
       price
      },
      type { productTypeId, name },
      thumbnail {
        fileId,
        content,
        type,
        name
      },
      packages {
        packageId,
        description,
        price,
      },
      productTypeAttribute {
        productTypeAttributeId,
        productTypeAttributeName,
        productTypeId
      }
    },
    reseller {
      userId, username, firstname, lastname, email
    },
    workflow {
      workflowId, finished, steps { stepId, datetime, name, notice, stepType, additionalInformation { id, key, value } }
    }`;

export interface MeshExportData {
  componentId: number | null;
  uniqueKey: string;
  name: string;
  category: UploadCategory;
  fileType: FileType3D;
  base64: string;
  thumbnailType: FileTypeImage;
  thumbnail: string;
  bones: BoneData[];
  color: string;
  position: THREE.Vector3;
  rotation: THREE.Euler;
  scale: THREE.Vector3;
  parentId: string | null;
  ffd: FFDExportData | null;
  embossing: EmbossingExportData[];
}

export interface CurveExportData {
  curveId: number | null;
  category: CurveCategory;
  name: string;
  closed: boolean;
  type: CurveType;
  meshId: string | null;
  position: THREE.Vector3;
  rotation: THREE.Euler;
  scale: THREE.Vector3;
  curvePoints: THREE.Vector3[];
}

export interface CameraExportData {
  cameraId: number | null;
  name: string;
  view: THREEEnum.Views;
  perspective: boolean;
  position: THREE.Vector3;
  rotation: THREE.Euler;
}

export enum TextureType {
  ENGRAVING = 'ENGRAVING',
  PRINT = 'PRINT',
}

export interface TextureFileType {
  name: string;
  type: string;
  content: string;
}

export interface TextureFileWithThumbnailType {
  hrFile: TextureFileType;
  lrFile: null
}

export interface TextureOptionsIdsType {
  fileId: number;
  thumbnailId: number | null;
}

export interface AddEngravingOrTextureDesignInputType {
  productId: number;
  textureType: TextureType;
  textureOptionsIds: TextureOptionsIdsType[];
  defaultDesign: TextureFileType | null;
  newFiles: TextureFileWithThumbnailType[];
  abortController: AbortController;
}

export interface MeshVectorSizeType {
  componentId: number;
  width: number;
  height: number;
  depth: number;
}

export interface ApplySizeToExistingProductInputType {
  productId: number;
  sizeInput: MeshVectorSizeType[];
  abortController: AbortController;
}

export type ComponentIdsType = {
  componentId: number; 
  uuid: string
}

export const parseToMeshExportData = (component: Component, resolutionLevel: Resolution_Level): MeshExportData => {
  let embossing: EmbossingExportData[] = [];
  if (component.embossing) {
    embossing = component.embossing.map((data) => {
      return {
        projectionType: data.projectionType ? (THREEEnum.ProjectionType[data.projectionType.toString() as keyof typeof THREEEnum.ProjectionType]) : THREEEnum.ProjectionType.cylinder,
        position: Vector3.convertToThreeVector(data.position),
        rotation: Vector3.convertToThreeEuler(data.rotation),
        scale: Vector3.convertToThreeVector(data.scale, new THREE.Vector3(1, 1, 1)),
        projection: {
          projectionCategory: (data.projectionCategory
            ? ProjectionCategory[data.projectionCategory as keyof typeof ProjectionCategory]
            : ProjectionCategory.custom) as ProjectionCategory,
          aspectRatio: data.aspectRatio,
          scaleFactor: data.scaleFactor,
          offset: data.offset,
          embossingFile: data.embossingFile,
          color: data.color,
          embossingDefinition: data.embossingDefinition.map((url) => {
            return {
              name: '',
              url: url,
              uid: 0,
              status: 'success',
            };
          }),
          colorDefinition: data.colorDefinition,
        },
      };
    });
  }

  return {
    componentId: component.componentId,
    uniqueKey: component.uuid,
    category: component.type ? (component.type.name as UploadCategory) : UploadCategory.ReferenceDummy,
    name: component.name,
    fileType: FileType3D.OBJ,
    base64: component.resolutions.length > 0 ? atob(resolutionLevel.meshBase64.content) : '',
    bones: component.markingPoints
      ? component.markingPoints.positions.map((point) => {
        return {
          name: point.markingPoint.name,
          position: Vector3.convertToThreeVector(point.position),
        };
      })
      : [],
    color: '#ffffff',
    thumbnailType: component.thumbnail ? UploadTypes.getImageFileType(component.thumbnail.type) : FileTypeImage.NONE,
    thumbnail: component.thumbnail ? component.thumbnail.content : '',
    position: Vector3.convertToThreeVector(component.position),
    rotation: Vector3.convertToThreeEuler(component.rotation),
    scale: Vector3.convertToThreeVector(component.scale, new THREE.Vector3(1, 1, 1)),
    parentId: component.parent ? component.parent.uuid : null,
    ffd: component.ffd
      ? {
        spanCounts: component.ffd.spanCounts,
        gridPositionList: component.ffd.gridPositionList.map((position) => Vector3.convertToThreeVector(position)),
      }
      : null,
    embossing: embossing,
  };
};

export const parseToCurveExportData = (curve: Curve, component: Component | null = null): CurveExportData => {
  return {
    curveId: curve.curveId,
    category: curve.category,
    name: curve.name,
    closed: curve.closed,
    type: curve.type,
    meshId: component ? component.uuid : null,
    position: Vector3.convertToThreeVector(curve.position),
    rotation: Vector3.convertToThreeEuler(curve.rotation),
    scale: Vector3.convertToThreeVector(curve.scale, new THREE.Vector3(1, 1, 1)),
    curvePoints: curve.points.map((point) => {
      return Vector3.convertToThreeVector(point);
    }),
  };
};

export const parseToCameraExportData = (camera: Camera): CameraExportData => {
  return {
    cameraId: camera.cameraId,
    name: camera.name,
    view: THREEEnum.Views.custom,
    perspective: true,
    position: Vector3.convertToThreeVector(camera.position),
    rotation: Vector3.convertToThreeEuler(camera.rotation),
  };
};

async function handleUserId(userId: number): Promise<number> {
  if (userId == LOGGED_IN_USER) {
    const authentication = Auth.getInstance();
    if (!authentication.user) {
      await authentication.handleAuthentication();

      while (!authentication.user && !authentication.hasRuntimeErrorOccured()) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
    }
    return authentication.user?.userId ?? -1;
  }
  return userId;
}

export const LOGGED_IN_USER = 0;

export const getModelOverviewList = async (): Promise<Model2[]> => {
  const result = await query(`
        query {
          modelsOverview {
            modelId, 
            notice,
            product {
              productId
              name,
              description
              thumbnail {
                content,
                type,
                name
              }
            },
            basedOn {
              modelId
            }
          }
        }
      `);

  return result.data.modelsOverview;
};


export const getModelsByUserId = async ({
  userId,
  productTypeId,
  modelTypeParameter = ModelTypes.ModelTypeParameter.BaseModel,
  shouldRefetch,
}: {
  userId: number;
  productTypeId?: number | null;
  modelTypeParameter?: ModelTypes.ModelTypeParameter;
  shouldRefetch?: boolean;
}): Promise<Model2[]> => {
  const data = {
    userId: await handleUserId(userId),
    productTypeId: productTypeId,
    modelTypeParameter: modelTypeParameter,
  };
  //

  const getProductsResult = await queryWithVariables(
    `
        query ($userId: Long!, $productTypeId: Long, $modelTypeParameter: ModelTypeParameter) {
          modelsByUserId (userId: $userId, productTypeId: $productTypeId, modelTypeParameter: $modelTypeParameter
    ) {
            modelId,
            notice,
            product {
              productId
              name,
              description
              sampleImagePaths {
                content,
                type,
                name
              }
              thumbnail {
                content,
                type,
                name
              },
              type {
                productTypeId,
                name
              },
              basePrices {
                country
                price
              },
              productTypeAttribute {
                productTypeAttributeId,
                productTypeAttributeName,
                productTypeId
              }
            },
            basedOn {
              modelId
            }
          }
        }
      `,
    data,
    new AbortController(),
    shouldRefetch
  );

  return getProductsResult.data.modelsByUserId;
};

export const getModelsByProductId = async (productId: number): Promise<Model2[]> => {
  const getModelsResult = await queryWithVariables(
    `
        query ($productId: Long!) {
          modelsByProductId (productId: $productId) {
            modelId,
            notice,
            product {
              productId
              name,
              description
              thumbnail {
                content,
                type,
                name
              },
              type {
                productTypeId,
                name
              },
              basePrices {
                country
                price
              },
              productTypeAttribute {
                productTypeAttributeId,
                productTypeAttributeName,
                productTypeId
              }
            },
            basedOn {
              modelId
            }
          }
        }
      `,
    {
      productId: productId,
    }
  );

  return getModelsResult.data.modelsByProductId;
};

export const getModelsByUrlSuffix = async (urlSuffix: string, productTypeId: number | null): Promise<Model2[]> => {
  const getProductsResult = await queryWithVariables(
    `
        query ($urlSuffix: String!, $productTypeId: Long) {
          modelsByUrlSuffix (urlSuffix: $urlSuffix, productTypeId: $productTypeId) {
            modelId, 
            notice,
            product {
              productId
              name,
              description
              thumbnail {
                content,
                type,
                name
              },
              type {
                productTypeId,
                name
              },
              basePrices {
                country
                price
              },
              productTypeAttribute {
                productTypeAttributeId,
                productTypeAttributeName,
                productTypeId
              }
            },
            basedOn {
              modelId
            }
          }
        }
      `,
    {
      urlSuffix: urlSuffix,
      productTypeId: productTypeId,
    }
  );

  return getProductsResult.data.modelsByUrlSuffix;
};

export const getMinResolutionLevel = async (modelId: number, productQuality: ProductQuality = ProductQuality.low): Promise<number> => {
  const result = await queryWithVariables(
    `
        query ($modelId: Long!) {
          resolutionLevels(modelId: $modelId)
        }
      `,
    {
      modelId: modelId,
    }
  );

  const levelList: number[] = [];
  for (const resolution of result.data.resolutionLevels) {
    levelList.push(resolution);
  }

  const sortedLevelList = levelList.sort((a, b) => (a < b ? -1 : 1));

  switch (productQuality) {
    case ProductQuality.original: {
      return sortedLevelList[0];
    }
    case ProductQuality.high: {
      return sortedLevelList[1];
    }
    case ProductQuality.middle: {
      return sortedLevelList[2];
    }
    case ProductQuality.low: {
      return sortedLevelList[3];
    }
    default: {
      return sortedLevelList[3];
    }
  }
};

export const getModels = async (minResolutionLevel: number): Promise<Model[]> => {
  const result = await queryWithVariables(
    `
        query ($minResolutionLevel: Int) {
          models (minResolutionLevel: $minResolutionLevel) {
            ${modelQueryParameter}
          }
        }
      `,
    {
      minResolutionLevel: minResolutionLevel,
    }
  );

  return result.data.models;
};

export const getModel = async (id: number, productQuality: ProductQuality = ProductQuality.low): Promise<Model> => {
  const minResolutionLevel = await getMinResolutionLevel(id, productQuality);
  const result = await queryWithVariables(
    `
        query ($id: Long!, $minResolutionLevel: Int) {
          models(ids: [$id], minResolutionLevel: $minResolutionLevel) {
            ${modelQueryParameter}
          }
        }
      `,
    {
      id: id,
      minResolutionLevel: minResolutionLevel,
    }
  );

  return result.data.models[0];
};

export const getModelProductData = async (
  id: number,
  productQuality: ProductQuality = ProductQuality.low
): Promise<Product> => {
  const minResolutionLevel = await getMinResolutionLevel(id, productQuality);
  const result = await queryWithVariables(
    `
        query ($id: Long!, $minResolutionLevel: Int) {
          models(ids: [$id], minResolutionLevel: $minResolutionLevel) {
            product {
              productId,
              name,
              description,
              type { productTypeId, name },
              thumbnail {
                fileId,
                content,
                type,
                name
              },
              packages {
                packageId,
                description,
                price,
              },
              ,
              basePrices {
                country
                price
              },
              productTypeAttribute {
                productTypeAttributeId,
                productTypeAttributeName,
                productTypeId
              }
            }
          }
        }
      `,
    {
      id: id,
      minResolutionLevel: minResolutionLevel,
    }
  );

  return result.data.models[0].product;
};

export const getModel2 = async (id: number, productQuality: ProductQuality = ProductQuality.low, abortController: AbortController): Promise<Model2> => {
  let minResolutionLevel = await getMinResolutionLevel(id, productQuality);
  if (!minResolutionLevel) minResolutionLevel = 1;
  const result = await queryWithVariables(
    `
        query ($id: Long!, $minResolutionLevel: Int) {
          models(ids: [$id], minResolutionLevel: $minResolutionLevel) {
            ${modelQueryParameter}
          }
        }
      `,
    {
      id: id,
      minResolutionLevel: minResolutionLevel,
    },
    abortController
  );

  return result.data.models[0];
};

export const getColor = async (componentId: number): Promise<Color> => {
  const result = await queryWithVariables(
    `
      query($componentId: Long!) {
        color(componentId: $componentId) {
          attributeId, additionalPrice, components { componentTypeId, name }, date, visualOnly, attributeType, supportedValues { paletteId, name, additionalPrice, supportedColors { name, red, green, blue } }, value { name, red, green, blue }
        }
      }
    `,
    {
      componentId: componentId,
    }
  );

  let color = result.data.color;
  if (color) {
    color = { ...result.data.color };
  }
  return color;
};

export const getSize = async (componentId: number): Promise<Size> => {
  const result = await queryWithVariables(
    `
      query($componentId: Long!) {
        size(componentId: $componentId) {
          attributeId, additionalPrice, components { componentTypeId, name }, date, visualOnly, attributeType, width, height, depth
        }
      }
    `,
    {
      componentId: componentId,
    }
  );

  let size = result.data.size;
  if (size) {
    size = { ...result.data.size };
  }
  return size;
};

export const getPower = async (componentId: number): Promise<Power> => {
  const result = await queryWithVariables(
    `
      query($componentId: Long!) {
        power(componentId: $componentId) {
          attributeId, additionalPrice, components { componentTypeId, name }, date, visualOnly, attributeType, value
        }
      }
    `,
    {
      componentId: componentId,
    }
  );

  let power = result.data.power;
  if (power) {
    power = { ...result.data.power };
  }
  return power;
};

export const getEngravingByProductType = async (productTypeId: number, textureType: string, shouldRefetch?: boolean): Promise<any[]> => {
  const result = await queryWithVariables(
    `
      query TextureOptions($productTypeId: Long, $textureType: TextureType) {
        textureOptions(productTypeId: $productTypeId, textureType: $textureType) {
          fileId
          productTypeId
          textureName
          textureOptionId
          textureType
          productType {
            name
            productTypeId
          }
          thumbnail {
            content
            fileId
            name
            type
          }
        }
      }
    `,
    {
      productTypeId: productTypeId,
      textureType: textureType,
    },
    undefined,
    shouldRefetch
  );

  let engraving: any[] = [];
  if (!result) return engraving;

  engraving = [...result.data.textureOptions]

  return engraving;
};

export const getEngraving = async (componentId: number, shouldRefetch?: boolean): Promise<Engraving> => {
  const result = await queryWithVariables(
    `
      query($componentId: Long!) {
        engraving(componentId: $componentId) {
          attributeId, 
          additionalPrice, 
          components { 
            componentTypeId, 
            name 
          }, 
          date, 
          visualOnly, 
          attributeType, 
          supportedValues {
            fileTextureId,
            file{fileId},
           
            thumbnail { 
              fileId, 
              name, 
              type, 
              content 
            }, 
           
            palette { 
              paletteId, 
              name,
              additionalPrice,
              supportedColors { 
                name, 
                red, 
                green, 
                blue 
              } 
            }
             
          }, 
          position { 
            xValue, 
            yValue, 
            zValue 
          }, 
          rotation { 
            xValue, 
            yValue, 
            zValue 
          }, 
          offset { 
            xValue, 
            yValue, 
            zValue 
          }, 
          engravingFile { 
            fileId, 
            name, 
            type, 
            content 
          }
        }
      }
    `,
    {
      componentId: componentId,
    },
    undefined,
    shouldRefetch
  );
  let engraving = result.data.engraving;
  if (engraving) {
    engraving = { ...result.data.engraving };
  }

  return engraving;
};

export const getEngravingFile = async (fileTextureId: number): Promise<UtilityFile> => {
  const result = await queryWithVariables(
    `
      query($fileTextureId: Long!) {
        engravingFile(fileTextureId: $fileTextureId) {
          fileId,
          name,
          content,
          textures{file{fileId}, thumbnail{fileId},fileTextureId}     
        }        
      }
    `,
    {
      fileTextureId: fileTextureId,
    }
  );
  let engravingFile = result.data.engravingFile;
  if (engravingFile) {
    engravingFile = { ...result.data.engravingFile };
  }

  return engravingFile;
};

export const getFile = async (fileId: number | null): Promise<UtilityFile> => {
  const result = await queryWithVariables(
    `
      query($fileId: Long!) {
        file(fileId: $fileId) {
          fileId,
          name,
          content,
        }        
      }
    `,
    {
      fileId: fileId,
    }
  );
  let file = result.data?.file;
  if (file) {
    file = { ...result.data.file };
  }

  return file;
};

export const getTexture = async (componentId: number, shouldRefetch?: boolean): Promise<Print> => {
  const result = await queryWithVariables(
    `
      query($componentId: Long!) {
        texture(componentId: $componentId) {
          attributeId, 
          additionalPrice, 
          components { 
            componentTypeId, 
            name 
          }, 
          date, 
          visualOnly, 
          attributeType, 
          supportedValues { 
            fileTextureId,
            file{fileId,name,content},
            thumbnail { 
              fileId, 
              name,
              type,
              content              
            }, 
            palette { 
              paletteId,
              name,
              additionalPrice,
              supportedColors { 
                name, 
                red, 
                green, 
                blue 
              } 
            }
          },
          position { 
            xValue, 
            yValue, 
            zValue 
          }, 
          rotation { 
            xValue, 
            yValue, 
            zValue 
          }, 
          offset { 
            xValue, 
            yValue,
            zValue 
          },
          transparent,
          textureFile { 
            fileId, 
            name, 
            type, 
            content 
          }
        }
      }
    `,
    {
      componentId: componentId,
    },
    undefined,
    shouldRefetch
  );

  let texture = result.data.texture;
  if (texture) {
    texture = { ...result.data.texture };
  }
  return texture;
};

export const undoSteps = async (modelId: number): Promise<number> => {
  const result = await queryWithVariables(
    `
        query ($modelId: Long!) {
          undoSteps(modelId: $modelId)
        }
      `,
    {
      modelId: modelId,
    }
  );

  return result.data.undoSteps;
};

//TODO: implement correctly
export const redoSteps = async (/*modelId: number*/): Promise<number> => {
  /*const result = await queryWithVariables(
    `
        query ($modelId: Long!) {
          redoSteps(modelId: $modelId)
        }
      `,
    {
      modelId: modelId,
    }
  );

  return result.data.undoSteps;*/
  return 0;
};

export const undo = async (modelId: number, numberOfSteps = 1): Promise<void> => {
  await mutateWithVariables(
    `
        mutation ($modelId: Long!, $numberOfSteps: Int!) {
          undo(
            input: { 
              modelId: $modelId,
              numberOfSteps: $numberOfSteps
            }
          ) { long }
        }
      `,
    {
      modelId: modelId,
      numberOfSteps: numberOfSteps,
    }
  );
};

export const redo = async (modelId: number, numberOfSteps = 1): Promise<void> => {
  await mutateWithVariables(
    `
        mutation ($modelId: Long!, $numberOfSteps: Int!) {
          redo(
            input: { 
              modelId: $modelId,
              numberOfSteps: $numberOfSteps
            }
          ) { long }
        }
      `,
    {
      modelId: modelId,
      numberOfSteps: numberOfSteps,
    }
  );
};

export const deleteModel = async (modelId: number): Promise<boolean> => {
  const result = await mutateWithVariables(
    `
        mutation ($modelId: Long!) {
          deleteModel(
            input: { modelId: $modelId }
          ) { boolean }
        }
      `,
    {
      modelId: modelId,
    }
  );
  return result.data.deleteModel.boolean;
};

export const deleteBaseModel = async (modelId: number, userId: number): Promise<boolean> => {
  const result = await mutateWithVariables(
    `
        mutation ($UserId:Long!, $ModelId:Long!) {
          deleteBaseModel(
            input: { modelId: $ModelId, userId: $UserId }
          ) { boolean }
        }
      `,
    {
      UserId: userId,
      ModelId: modelId,
    }
  );
  return result.data.deleteBaseModel.boolean;
};

export const automaticAdaption = async (
  productId: number,
  scanFileId: number,
  skeletonFileId: number,
  size: UtilitySize,
  marginDistance = 0.0,
  keepOutside = false,
  resolutionLevel = 16,
  side = 2
): Promise<Model2> => {
  const automaticAdaption = await mutateWithVariables(
    `
        mutation ($productId: Long!, $scanFileId: Long!, $skeletonFileId: Long!, $width: Float!, $height: Float!, $depth: Float!, $marginDistance: Float, $keepOutside: Boolean, $resolutionLevel: Int, $side: Int) {
          automaticAdaption(
            input: {
              productId: $productId,
              scanFileId: $scanFileId,
              skeletonFileId: $skeletonFileId,
              size: {
                width: $width,
                height: $height,
                depth: $depth
              },
              marginDistance: $marginDistance,
              keepOutside: $keepOutside,
              resolutionLevel: $resolutionLevel,
              side: $side
            })
          {
            model {
              ${modelQueryParameter}
            }
          }
        }
      `,
    {
      productId: productId,
      scanFileId: scanFileId,
      skeletonFileId: skeletonFileId,
      width: size.width,
      height: size.height,
      depth: size.depth,
      marginDistance: marginDistance,
      keepOutside: keepOutside,
      resolutionLevel: resolutionLevel,
      side: side,
    }
  );

  return automaticAdaption.data.automaticAdaption.model;
};

export const createNewBalconyModel = async (
  baseModelId: number,
  width: number,
  height: number,
  depthLeft: number,
  depthRight: number,
  type: string,
  userId: number,
  abortController: AbortController
): Promise<Model2> => {
  const result = await mutateWithVariables(
    `
        mutation ($baseModelId: Long!, $width: Float!, $height: Float!, $depthLeft: Float!, $depthRight: Float!, $type: String!, $userId: Long!) {
          createNewBalconyModel(
            input: { 
              baseModelId: $baseModelId,
              width: $width,
              height: $height,
              depthLeft: $depthLeft,
              depthRight: $depthRight,
              type: $type,
              userId: $userId,
            }
          ) 
          { 
            model {
              ${modelQueryParameter}
            }
          }
        }
      `,
    {
      baseModelId: baseModelId,
      width: width,
      height: height,
      depthLeft: depthLeft,
      depthRight: depthRight,
      type: type,
      userId: userId,
    },
    abortController
  );
  return result.data.createNewBalconyModel.model;
};

export const createNewFenceSunModel = async (
  baseModelId: number,
  width: number,
  height: number,
  depthLeft: number,
  depthRight: number,
  type: string,
  orientation: string,
  userId: number,
  abortController: AbortController
): Promise<Model2> => {
  if (type === FenceType.uForm) {
    depthRight = depthLeft;
  }
  const result = await mutateWithVariables(
    `
        mutation ($baseModelId: Long!, $width: Float!, $height: Float!, $depthLeft: Float!, $depthRight: Float!, $type: String!, $orientation: String!, $userId: Long!) {
          createNewFenceSunModel(
            input: { 
              baseModelId: $baseModelId,
              width: $width,
              height: $height,
              depthLeft: $depthLeft,
              depthRight: $depthRight,
              type: $type,
              orientation: $orientation,
              userId: $userId
            }
          ) 
          { 
            model {
              ${modelQueryParameter}
            }
          }
        }
      `,
    {
      baseModelId: baseModelId,
      width: width,
      height: height,
      depthLeft: depthLeft,
      depthRight: depthRight,
      type: type,
      orientation: orientation,
      userId: userId,
    },
    abortController
  );

  return result.data.createNewFenceSunModel.model;
};

export const createNewFenceModel = async (
  baseModelId: number,
  width: number,
  height: number,
  depth: number,
  type: string,
  userId: number,
  abortController: AbortController
): Promise<Model2> => {
  const result = await mutateWithVariables(
    `
        mutation ($baseModelId: Long!, $width: Float!, $height: Float!, $depth: Float!, $type: String!, $userId: Long!) {
          createNewFenceModel(
            input: { 
              baseModelId: $baseModelId,
              width: $width,
              height: $height,
              depth: $depth,
              type: $type,
              userId: $userId,
            }
          ) 
          { 
            model {
              ${modelQueryParameter}
            }
          }
        }
      `,
    {
      baseModelId: baseModelId,
      width: width,
      height: height,
      depth: depth,
      type: type,
      userId: userId,
    },
    abortController
  );
  return result.data.createNewFenceModel.model;
};

export const createNewSuspendedLightModel = async (
  baseModelId: number,
  color: string,
  mountingProfileType: string,
  lightCoverType: string,
  vFour: boolean,
  iPRatingType: string,
  lightColorType: string,
  lightLength: number,
  abortController: AbortController
): Promise<Model2> => {
  const result = await mutateWithVariables(
    `
        mutation 
        (
          $baseModelId: Long!,
          $color: String!,
          $mountingProfileType: String!,
          $lightCoverType: String!,
          $vFour: Boolean!,
          $iPRatingType: String!,
          $lightColorType: String!,
          $lightLength: Int!
        ) {
          createNewSuspendedLightModel(
            input: { 
              baseModelId: $baseModelId,
              color: $color,
              mountingProfileType: $mountingProfileType,
              lightCoverType: $lightCoverType,
              vFour: $vFour,
              iPRatingType: $iPRatingType,
              lightColorType: $lightColorType,
              lightLength: $lightLength
            }
          ) 
          { 
            model {
              ${modelQueryParameter}
            }
          }
        }
      `,
    {
      baseModelId: baseModelId,
      color: color,
      mountingProfileType: mountingProfileType,
      lightCoverType: lightCoverType,
      vFour: vFour,
      iPRatingType: iPRatingType,
      lightColorType: lightColorType,
      lightLength: lightLength,
    },
    abortController
  );
  return result.data.createNewSuspendedLightModel.model;
};

export const createNewBathtubModel = async (
  baseModelId: number,
  bathtubType: string,
  wallpanels: boolean,
  panelVariant: string,
  singleType: string[],
  comboType: string,
  userId: number,
  abortController: AbortController
): Promise<Model2> => {
  const result = await mutateWithVariables(
    `
        mutation 
        (
          $baseModelId: Long!,
          $bathtubType: String!,
          $wallpanels: Boolean!,
          $panelVariant: String!,
          $singleType: [String!]!,
          $comboType: String!,
          $userId: Long!,
        ) {
          createNewBathtubModel(
            input: { 
              baseModelId: $baseModelId,
              bathtubType: $bathtubType,
              wallpanels: $wallpanels,
              panelVariant: $panelVariant,
              singleType: $singleType,
              comboType: $comboType,
              userId: $userId,
            }
          ) 
          { 
            model {
              ${modelQueryParameter}
            }
          }
        }
      `,
    {
      baseModelId: baseModelId,
      bathtubType: bathtubType,
      wallpanels: wallpanels,
      panelVariant: panelVariant,
      singleType: singleType,
      comboType: comboType,
      userId: userId,
    },
    abortController
  );
  return result.data.createNewBathtubModel.model;
};

const defaultTexture = {
  position: {
    xValue: 0,
    yValue: 0,
    zValue: 0,
  },
  rotation: {
    xValue: 0,
    yValue: 0,
    zValue: 0,
  },
  offset: {
    xValue: 0,
    yValue: 0,
    zValue: 0,
  },
  transparent: false,
  supportedValues: [],
  attributeType: 'TEXTURE',
  date: '2023-02-22T00:00:00.000Z',
  visualOnly: true,
  additionalPrice: 0,
  components: [],
};

const defaultEngravingOrTexturePayload = {
  position: {
    xValue: 0,
    yValue: 0,
    zValue: 0,
  },
  rotation: {
    xValue: 0,
    yValue: 0,
    zValue: 0,
  },
  offset: {
    xValue: 0,
    yValue: 0,
    zValue: 0,
  },
  additionalPrice: 0,
  visualOnly: true,
  transparent: false,
  components: [],
};

export interface ImageFileType {
  id: number;
  name: string;
  type: string;
  content: string;
}

export const addEngravingToModel = async ({
  productId,
  engravingImageFilesLR,
  engravingImageFilesHR,
  abortController,
}: {
  productId: number;
  engravingImageFilesLR: Omit<ImageFileType, 'id'>[];
  engravingImageFilesHR: Omit<ImageFileType, 'id'>[];
  abortController: AbortController;
}): Promise<boolean> => {

  try {
    const now = new Date().toISOString();
    const supportedValues = engravingImageFilesLR.map((thumbnail, i) => {
      const name = thumbnail.name?.includes('.') ? thumbnail.name.split('.')[0] : thumbnail.name;
      const fileName = engravingImageFilesHR[i].name?.includes('.') ? engravingImageFilesHR[i].name.split('.')[0] : engravingImageFilesHR[i].name;
      return {
        thumbnail: {
          name,
          type: thumbnail.type,
          content: thumbnail.content,
          textures: [],
          thumbnailTextures: [],
        },
        file: {
          name: fileName,
          type: engravingImageFilesHR[i].type,
          content: engravingImageFilesHR[i].content,
          textures: [],
          thumbnailTextures: [],
        },
        texture: defaultTexture,
      };
    });
    const result = await mutateWithVariables(
      `
        mutation AddEngravingDesign($in: AddEngravingDesignInput!) {
          addEngravingDesign(input: $in) {
            long
          }
        }
      `,
      {
        in: {
          productId,
          engraving: {
            ...defaultEngravingOrTexturePayload,
            date: now,
            supportedValues,
            attributeType: 'ENGRAVING',
          },
        },
      },
      abortController
    );
    return result.data;
  } catch (_error) {
    console.error('_error', _error)
    return false;
  }
};

export const addTextureToModel = async ({
  productId,
  textureImageFilesLR,
  textureImageFilesHR,
  abortController,
}: {
  productId: number;
  textureImageFilesLR: Omit<ImageFileType, 'id'>[];
  textureImageFilesHR: Omit<ImageFileType, 'id'>[];
  abortController: AbortController;
}): Promise<boolean> => {
  try {
    const now = new Date().toISOString();

    // Note: comment this in case we still need this blank when uploading template, do not forget to add this to addEngravingToModel as well if its being used.
    // const createTexturePayload = {
    //   ...defaultEngravingOrTexturePayload,
    //   textureFile: blankFile,
    // };
    // const blankSupportedValue = {
    //   thumbnail: createTexturePayload.textureFile,
    //   file: createTexturePayload.textureFile,
    //   texture: defaultTexture,
    // };


    const supportedValues = textureImageFilesLR.map((thumbnail, i) => {
      const name = thumbnail.name?.includes('.') ? thumbnail.name.split('.')[0] : thumbnail.name;
      const fileName = textureImageFilesHR[i].name?.includes('.') ? textureImageFilesHR[i].name.split('.')[0] : textureImageFilesHR[i].name;
      return {
        thumbnail: {
          name,
          type: thumbnail.type,
          content: thumbnail.content,
          textures: [],
          thumbnailTextures: [],
        },
        file: {
          name: fileName,
          type: textureImageFilesHR[i].type,
          content: textureImageFilesHR[i].content,
          textures: [],
          thumbnailTextures: [],
        },
        texture: defaultTexture,
      };
    });
    const result = await mutateWithVariables(
      `
        mutation AddTextureDesign($in: AddTextureDesignInput!) {
          addTextureDesign(input: $in) {
            long
          }
        }
      `,
      {
        in: {
          productId,
          texture: {
            ...defaultEngravingOrTexturePayload,
            date: now,
            // Note: comment this in case we still need this blank when uploading template
            // supportedValues: [blankSupportedValue, ...supportedValues],
            supportedValues,
            attributeType: 'attributeType',
          },
        },
      },
      abortController
    );
    return result.data;
  } catch (_error) {
    console.error('_error', _error)
    return false;
  }
};

//  TODOS: ADJUST THIS LATER, PAYLOAD IS STILL WRONG
const defaultColorPayload = {
  additionalPrice: 0,
};

export const applyColorDesignToExistingProduct = async ({
  productId,
  selectedColors,
  title,
  abortController,
}: {
  productId: number;
  title: string;
  selectedColors: LocalColorType[];
  abortController: AbortController;
}): Promise<boolean> => {
  try {
    const defaultColor = {
      blue: 188,
      green: 220,
      red: 241,
      name: "skin"
    }
    const supportedColors = selectedColors.map((color) => ({
      blue: color.blue,
      green: color.green,
      red: color.red,
      name: color.name
    }))
    const supportedValues = {
      additionalPrice: 0,
      name: title,
      supportedColors: supportedColors,
      color: [],
    }
    const result = await mutateWithVariables(
      `
        mutation ApplyColorDesignToExistingProduct($in: ApplyColorDesignToExistingProductInput!) {
          applyColorDesignToExistingProduct(input: $in) {
            boolean
          }
        }
      `,
      {
        in: {
          productId,
          colorInput: {
            ...defaultColorPayload,
            supportedValues,
            value: defaultColor
          },
        }
      },
      abortController
    );
    return result.data;
  } catch (_error) {
    console.error('_error', _error)
    return false;
  }
};

export const deleteOneEngravingDesign = async ({ fileTextureId, abortController }: { fileTextureId: number; abortController: AbortController }) => {
  const result = await mutateWithVariables(
    `
      mutation deleteEngraving($in: DeleteOneEngravingDesignInput!) {
        deleteOneEngravingDesign(input: $in) {
          boolean
        }
      }
    `,
    {
      in: {
        fileTextureId,
      },
    },
    abortController
  );

  return result.data;
};

export const getProductTypeIdsByUrlSuffix = async (urlSuffix: string, productTypeId: number): Promise<{ suffix: string; pti: number }> => {
  const response = await queryWithVariables(
    `
      query GetProductTypeIdsByUrlSuffix($urlSuffix: String!) {
        productTypeIdsByUrlSuffix(urlSuffix: $urlSuffix)
      }
    `,
    {
      urlSuffix: urlSuffix,
    }
  );

  const defaultResponse = { suffix: 'aidddo', pti: 1 };

  if (!response?.data?.productTypeIdsByUrlSuffix || response.data.productTypeIdsByUrlSuffix.length === 0) {
    return defaultResponse;
  }

  const productTypeIds = response.data.productTypeIdsByUrlSuffix;  

  if (!productTypeIds.includes(productTypeId)) {
    return { suffix: urlSuffix, pti: productTypeIds[0] };
  }

  return { suffix: urlSuffix, pti: productTypeId };
};

export const getAllProductTypeIdsByUrlSuffix = async (urlSuffix: string): Promise<number[]> => {
  const response = await queryWithVariables(
    `
      query GetProductTypeIdsByUrlSuffix($urlSuffix: String!) {
        productTypeIdsByUrlSuffix(urlSuffix: $urlSuffix)
      }
    `,
    { urlSuffix }
  );

  return response?.data?.productTypeIdsByUrlSuffix || [1];
};

export const AddEngravingOrTextureDesign = async ({
  productId,
  textureType,
  textureOptionsIds,
  defaultDesign,
  newFiles,
  abortController,
}: AddEngravingOrTextureDesignInputType): Promise<boolean> => {

  // ✅ productId validation
  if (!productId || productId <= 0) {
    console.error('Invalid productId:', productId);
    return false;
  }

  // ✅ validTextureTypes validation
  const validTextureTypes: TextureType[] = [TextureType.ENGRAVING, TextureType.PRINT];
  if (!validTextureTypes.includes(textureType)) {
    console.error('Invalid textureType:', textureType);
    return false;
  }

  // ✅ Payload 
  const textureInputRequest = {
    textureType,
    textureOptionsIds,
    defaultDesign,
    newFiles
  }

  try {

    // ✅ Mutasi GraphQL
    const result = await mutateWithVariables(
      `
      mutation ApplyTextureDesignToExistingProduct($input: ApplyTextureDesignToExistingProductInput!) {
        applyTextureDesignToExistingProduct(input: $input) {
          boolean
        }
      }
      `,
      {
        input: {
          productId,
          textureInputRequest
        },
      },
      abortController
    );

    return result?.data?.applyTextureDesignToExistingProduct?.boolean ?? false;

  } catch (error) {
    console.error('AddEngravingOrTextureDesign failed:', error);
    return false;
  }
};

export const applySizeToExistingProduct = async ({
  productId,
  sizeInput,
  abortController
}: ApplySizeToExistingProductInputType): Promise<boolean> => {

  // ✅ productId validation
  if (!productId || productId <= 0) {
    console.error('Invalid productId:', productId);
    return false;
  }

  // ✅ sizeInput validation
  if (!sizeInput || sizeInput.length === 0) {
    console.error('sizeInput empty:', sizeInput);
    return false;
  }
  
  try {
    // ✅ Mutasi GraphQL
    const result = await mutateWithVariables(
      `
      mutation ApplySizeToExistingProduct($input: ApplySizeToExistingProductInput!) {
        applySizeToExistingProduct(input: $input) {
          boolean  
        }
      }
      `,
      {
        input: {
          productId,
          sizeInput
        },
      },
      abortController
    );

    return result?.data?.applySizeToExistingProduct?.boolean ?? false;

  } catch (error) {
    console.error('applySizeToExistingProduct failed:', error);
    return false;
  }

}
